/**
 * Discord-style chat search syntax, parsed on the client (PLAN D2): the input
 * is plain text plus filter tokens —
 *
 *   has:image from:"Kenan Kobic" before:2026-09-01 invoice
 *
 * The keys and the `has:` values are English syntax constants in every
 * language (D21); only the menu labels next to them are translated. The
 * server never sees this syntax, only the structured params built by
 * `chatSearchParams.toSearchParams`.
 */

export const FILTER_KEYS = ['from', 'has', 'before', 'after', 'during'];
export const DATE_KEYS = ['before', 'after', 'during'];

/**
 * The `has:` values the search endpoint accepts (PLAN §5.3 + owner Q2), in menu
 * order. `icon` names a lucide icon; the component maps it. `forward` only
 * finds forwards sent since the marker was introduced — the menu says so.
 */
export const HAS_VALUES = [
  { value: 'image', icon: 'Image' },
  { value: 'video', icon: 'Video' },
  { value: 'file', icon: 'Paperclip' },
  { value: 'link', icon: 'Link2' },
  { value: 'code', icon: 'Code' },
  { value: 'task', icon: 'CheckSquare' },
  { value: 'appointment', icon: 'CalendarDays' },
  { value: 'workspace', icon: 'Layers' },
  { value: 'collection', icon: 'Folder' },
  { value: 'request', icon: 'Send' },
  { value: 'document', icon: 'FileText' },
  { value: 'board', icon: 'Kanban' },
  { value: 'secret', icon: 'Lock' },
  { value: 'reply', icon: 'Reply' },
  { value: 'forward', icon: 'Forward' },
].map((h) => ({ ...h, labelKey: `chat.search.has.${h.value}` }));

const HAS_SET = new Set(HAS_VALUES.map((h) => h.value));
const KEY_SET = new Set(FILTER_KEYS);

const isSpace = (c) => c === ' ' || c === '\t' || c === '\n' || c === '\r';

/**
 * Split the input into tokens with their source ranges. A token is either
 * `key:value` (known key, value optionally "quoted"), a "quoted phrase", or a
 * run of non-space characters. An unterminated quote runs to the end.
 */
export function scanTokens(str) {
  const s = String(str || '');
  const out = [];
  let i = 0;
  while (i < s.length) {
    if (isSpace(s[i])) {
      i += 1;
      continue;
    }
    const start = i;
    const colon = /^([A-Za-z]+):/.exec(s.slice(i));
    const key = colon ? colon[1].toLowerCase() : null;
    if (key && KEY_SET.has(key)) {
      let j = i + colon[0].length;
      let value;
      let quoted = false;
      if (s[j] === '"') {
        quoted = true;
        const close = s.indexOf('"', j + 1);
        const end = close === -1 ? s.length : close + 1;
        value = s.slice(j + 1, close === -1 ? s.length : close);
        j = end;
      } else {
        const valStart = j;
        while (j < s.length && !isSpace(s[j])) j += 1;
        value = s.slice(valStart, j);
      }
      out.push({ start, end: j, raw: s.slice(start, j), key, value, quoted });
      i = j;
      continue;
    }
    let j = i;
    if (s[i] === '"') {
      const close = s.indexOf('"', i + 1);
      j = close === -1 ? s.length : close + 1;
    } else {
      while (j < s.length && !isSpace(s[j])) j += 1;
    }
    out.push({ start, end: j, raw: s.slice(start, j), key: null, value: null, quoted: false });
    i = j;
  }
  return out;
}

/**
 * → { text, has[], from[], before[], after[], during[] }. An unknown `has:`
 * value stays plain text (so `has:sticker` is searched for literally); a known
 * key with an empty value is still being typed and is dropped.
 */
export function parseSearchQuery(str) {
  const parsed = { text: '', has: [], from: [], before: [], after: [], during: [] };
  const text = [];
  for (const tok of scanTokens(str)) {
    if (!tok.key) {
      text.push(tok.raw);
      continue;
    }
    const value = tok.value.trim();
    if (!value) continue;
    if (tok.key === 'has') {
      const v = value.toLowerCase();
      if (HAS_SET.has(v)) {
        if (!parsed.has.includes(v)) parsed.has.push(v);
      } else {
        text.push(tok.raw);
      }
      continue;
    }
    parsed[tok.key].push(value);
  }
  parsed.text = text.join(' ');
  return parsed;
}

const quoteIfNeeded = (v) => (/\s/.test(v) ? `"${v}"` : v);

/** Inverse of parseSearchQuery (canonical order: from, has, dates, text). */
export function serializeSearchQuery(parsed) {
  const parts = [];
  (parsed.from || []).forEach((v) => parts.push(`from:${quoteIfNeeded(v)}`));
  (parsed.has || []).forEach((v) => parts.push(`has:${v}`));
  DATE_KEYS.forEach((k) => (parsed[k] || []).forEach((v) => parts.push(`${k}:${quoteIfNeeded(v)}`)));
  if (parsed.text) parts.push(parsed.text);
  return parts.join(' ');
}

/**
 * What the autocomplete should offer for the caret position:
 *   { mode: 'keys' | 'has' | 'from' | 'date', key, partial, range: [start, end] }
 * or null (inside a quoted phrase or a token with an unknown `key:`).
 * `range` is the source span a chosen suggestion replaces.
 */
export function autocompleteContext(str, caret) {
  const s = String(str || '');
  const pos = Math.max(0, Math.min(caret ?? s.length, s.length));
  const tok = scanTokens(s).find((t) => t.start <= pos && pos <= t.end);
  if (!tok) return { mode: 'keys', key: null, partial: '', range: [pos, pos] };
  if (tok.key) {
    const valueStart = tok.start + tok.key.length + 1 + (tok.quoted ? 1 : 0);
    if (pos < valueStart - (tok.quoted ? 1 : 0)) {
      // caret inside the key itself: offer keys
      return { mode: 'keys', key: null, partial: s.slice(tok.start, pos).toLowerCase(), range: [tok.start, tok.end] };
    }
    const partial = s.slice(valueStart, Math.max(valueStart, pos)).replace(/"$/, '');
    const mode = tok.key === 'has' ? 'has' : tok.key === 'from' ? 'from' : 'date';
    return { mode, key: tok.key, partial, range: [tok.start, tok.end] };
  }
  if (tok.raw.startsWith('"') || tok.raw.includes(':')) return null;
  return { mode: 'keys', key: null, partial: s.slice(tok.start, pos).toLowerCase(), range: [tok.start, tok.end] };
}

/**
 * Replace `range` with `token`. A complete filter (`has:task`) gets a
 * trailing space so typing continues with the next term; a bare key
 * (`has:`) does not, so its value menu opens right away.
 */
export function applySuggestion(str, range, token) {
  const s = String(str || '');
  const [a, b] = range;
  const before = s.slice(0, a);
  const after = s.slice(b);
  if (token.endsWith(':')) return { str: before + token + after, caret: a + token.length };
  if (/^\s/.test(after)) return { str: before + token + after, caret: a + token.length + 1 };
  return { str: `${before}${token} ${after}`, caret: a + token.length + 1 };
}

/** A member's display fields, tolerant of both `{user:{…}}` and flat shapes. */
export function memberInfo(member) {
  const u = member?.user || member || {};
  return { id: member?.user_id ?? u.id, name: u.name || '', email: u.email || '' };
}

/**
 * Map `from:` names to user ids: case-insensitive exact name first, then the
 * e-mail local part. A name shared by several members adds all of them.
 */
export function resolveFromNames(names, members) {
  const infos = (members || []).map(memberInfo).filter((m) => m.id != null);
  const ids = [];
  const unresolved = [];
  for (const raw of names || []) {
    const n = String(raw).trim().toLowerCase();
    let hits = infos.filter((m) => m.name.toLowerCase() === n);
    if (!hits.length) hits = infos.filter((m) => m.email.split('@')[0].toLowerCase() === n);
    if (!hits.length) {
      unresolved.push(raw);
      continue;
    }
    hits.forEach((m) => {
      if (!ids.includes(m.id)) ids.push(m.id);
    });
  }
  return { ids, unresolved };
}
